import { lazy, StrictMode, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import './styles/globals.css';
import { initTheme } from './lib/theme';

initTheme();

import { LandingPage } from './pages/landing/LandingPage';
import { SignInPage } from './pages/auth/SignInPage';
import { SignUpPage } from './pages/auth/SignUpPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { DashboardPage } from './pages/app/DashboardPage';
import { NewDraftPage } from './pages/app/NewDraftPage';
import { SettingsPage } from './pages/app/SettingsPage';
import { SentPage } from './pages/app/SentPage';
import { BrandPage } from './pages/app/BrandPage';
import { PrivacyPage } from './pages/legal/PrivacyPage';
import { TermsPage } from './pages/legal/TermsPage';
import { DocsPage } from './pages/docs/DocsPage';
import { InstallPage } from './pages/install/InstallPage';
import { useSession } from './hooks/useSession';

// Admin lives behind a flag (ADMIN_EMAILS) and is rarely loaded — split it
// out of the main bundle so logged-in users never download it unless they
// are an admin who navigates to /admin.
const AdminPage = lazy(() =>
  import('./pages/admin/AdminPage').then((m) => ({ default: m.AdminPage })),
);

// Editor pulls in GrapesJS (~1.5 MB). Keep it out of the landing page bundle.
const EditorPage = lazy(() =>
  import('./pages/app/EditorPage').then((m) => ({ default: m.EditorPage })),
);

function EditorFallback() {
  return (
    <div className="text-muted-foreground flex min-h-screen items-center justify-center gap-2 text-sm">
      <Loader2 className="size-5 animate-spin" aria-hidden />
      Loading editor…
    </div>
  );
}

function AppShellGuard() {
  const { user, loading } = useSession();
  if (loading) {
    return (
      <div className="text-muted-foreground flex min-h-screen items-center justify-center gap-2 text-sm">
        <Loader2 className="size-5 animate-spin" aria-hidden />
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/sign-in" replace />;
  return <Outlet />;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-up" element={<SignUpPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/install" element={<InstallPage />} />
        <Route path="/app" element={<AppShellGuard />}>
          <Route index element={<DashboardPage />} />
          <Route path="new" element={<NewDraftPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="sends" element={<SentPage />} />
          <Route path="brand" element={<BrandPage />} />
          <Route
            path="d/:draftId"
            element={
              <Suspense fallback={<EditorFallback />}>
                <EditorPage />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="/admin"
          element={
            <Suspense
              fallback={
                <div className="text-muted-foreground flex min-h-screen items-center justify-center gap-2 text-sm">
                  <Loader2 className="size-5 animate-spin" aria-hidden />
                  Loading admin…
                </div>
              }
            >
              <AdminPage />
            </Suspense>
          }
        />
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  </StrictMode>,
);
